import { createContext, useContext, type ReactNode } from 'react';
import { useDonnees, type EtatDonnees } from './donnees.ts';

/**
 * Année scolaire active.
 *
 * Presque toutes les données de l'application y sont rattachées : élèves,
 * séances, progressions, emploi du temps. Le serveur la déduit lui-même à
 * chaque requête, donc l'interface n'a pas à la transmettre — mais elle doit la
 * connaître pour deux raisons :
 *
 * 1. **Afficher de quoi on parle.** Un enseignant qui prépare la rentrée
 *    suivante doit voir immédiatement sur quelle année il travaille.
 * 2. **Ne pas ouvrir des écrans qui échoueront.** Sans année active, la moitié
 *    des routes répondent « Aucune année scolaire active ». Autant le dire une
 *    fois, avec le bouton pour y remédier, plutôt qu'à chaque écran.
 */

export interface AnneeScolaire {
  id: string;
  label: string;
  startsOn: string;
  endsOn: string;
  active: boolean;
  /**
   * Édition du programme suivie par cette année.
   *
   * `null` = la publication la plus récente. Elle vient de la rentrée ouverte
   * : l'école ne la choisit pas, elle en hérite.
   *
   * ── N'est PLUS affichée ───────────────────────────────────────────────────
   * Le nom d'une édition — « Programme 2020 » — ne dit rien à un enseignant :
   * il ne l'a pas choisie, ne peut pas en changer, et ce qu'elle recouvre se
   * lit dans les objectifs proposés, pas dans son intitulé. Le montrer faisait
   * poser la question au lieu d'y répondre.
   *
   * Le champ reste lu : le back-office s'en sert, et c'est lui qui décide.
   */
  curriculumLabel: string | null;
}

const Contexte = createContext<EtatDonnees<AnneeScolaire> | null>(null);

export function FournisseurAnnee({ children }: { children: ReactNode }) {
  const etat = useDonnees<AnneeScolaire>('/school-years/active');
  return <Contexte.Provider value={etat}>{children}</Contexte.Provider>;
}

export function useAnneeActive(): EtatDonnees<AnneeScolaire> {
  const contexte = useContext(Contexte);
  if (!contexte) throw new Error('useAnneeActive doit être utilisé dans un FournisseurAnnee');
  return contexte;
}

/**
 * L'année active si l'écran est sous un `FournisseurAnnee`, sinon `null`.
 *
 * Pour un détail facultatif — l'année dans le titre d'une impression — qui ne
 * doit pas empêcher l'écran de s'afficher hors de ce fournisseur.
 */
export function useAnneeFacultative(): AnneeScolaire | null {
  return useContext(Contexte)?.donnees ?? null;
}
